import React from 'react'
import { Box, Chip, Typography } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { useStalledAfterDays } from '../api/AdvancedSettingsProvider'
import { AdvancedTrackSummary } from '../api/types'
import { isTrackRunning, lastCompletedThisRun } from '../api/selectors'
import { TRACK_STATE_COLORS, advancedColors } from '../theme'
import { formatDay, relativeDay } from '../format'
import { trackStatus, trackStatusLabel } from '../dashboard/tracks/trackTransitions'
import EmptyState from './EmptyState'

type TitleFor = (trackKey: string) => string

const captionFor = (track: AdvancedTrackSummary, titleFor: TitleFor, stalledAfterDays: number): string => {
  if (track.endedAt) {
    return [
      trackStatusLabel(track, stalledAfterDays),
      track.nextTrackKey && `continued in ${titleFor(track.nextTrackKey)}`,
    ]
      .filter(Boolean)
      .join(' · ')
  }
  if (!isTrackRunning(track)) {
    return trackStatusLabel(track, stalledAfterDays)
  }
  const last = lastCompletedThisRun(track)
  const progress = last
    ? `Last completed ${relativeDay(last)}`
    : `Started ${formatDay(track.startedAt)} · nothing completed yet`

  return track.previousTrackKey ? `${progress} · after ${titleFor(track.previousTrackKey)}` : progress
}

// Active first, then most recently ended.
const byRelevance = (a: AdvancedTrackSummary, b: AdvancedTrackSummary) => {
  if (!a.endedAt || !b.endedAt) {
    return Number(Boolean(a.endedAt)) - Number(Boolean(b.endedAt))
  }

  return b.endedAt.localeCompare(a.endedAt)
}

type LeadFor = (track: AdvancedTrackSummary) => string | null

type RowProps = {
  track: AdvancedTrackSummary
  titleFor: TitleFor
  leadFor?: LeadFor
}

const TrackRow = ({ track, titleFor, leadFor }: RowProps) => {
  const stalledAfterDays = useStalledAfterDays()
  const lead = leadFor?.(track)
  const state = trackStatus(track, stalledAfterDays)
  const title = titleFor(track.trackKey)

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          mt: 0.75,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: state === 'completed' ? advancedColors.green : advancedColors.surface,
          border: `2px solid ${TRACK_STATE_COLORS[state]}`,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="h6"
            sx={{
              color: state === 'closed' ? advancedColors.inkMuted : advancedColors.ink,
              fontWeight: state === 'active' ? 700 : 500,
            }}
          >
            {title}
          </Typography>
          {state === 'completed' && <CheckRoundedIcon sx={{ fontSize: 16, color: advancedColors.green }} />}
          {state === 'stalled' && <Chip size="small" label="Stalled" sx={{ height: 20 }} />}
          {state === 'closed' && <Chip size="small" variant="outlined" label="Closed" sx={{ height: 20 }} />}
        </Box>
        <Typography variant="subtitle2" sx={{ color: advancedColors.ink }}>
          {track.completedCount} completed · {captionFor(track, titleFor, stalledAfterDays)}
        </Typography>
        {leadFor && (
          <Typography variant="subtitle2" sx={{ color: lead ? advancedColors.inkMuted : advancedColors.inkFaint }}>
            {lead ? `Led by ${lead}` : !track.endedAt && 'No lead yet'}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

type Props = {
  tracks: AdvancedTrackSummary[]
  titleFor?: TitleFor
  leadFor?: LeadFor
}

const TrackProgressList = ({ tracks, titleFor = (trackKey) => trackKey, leadFor }: Props) => {
  if (tracks.length === 0) {
    return <EmptyState title="No tracks yet" description="This patient is not enrolled in any track." />
  }

  return (
    <Box>
      {[...tracks].sort(byRelevance).map((track) => (
        <TrackRow key={track.trackKey} track={track} titleFor={titleFor} leadFor={leadFor} />
      ))}
    </Box>
  )
}

export default TrackProgressList
